import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'

type ParticipantStatus = {
  isParticipantPresent: boolean
  isLoading: boolean
}

/**
 * Hook to detect if the other participant is already in the meeting.
 *
 * In production: Would query the backend or use Chime SDK to check active participants
 * On /demo route: Returns a mocked random status for testing
 *
 * @returns {ParticipantStatus} Object containing participant presence status and loading state
 */
export const useParticipantStatus = (): ParticipantStatus => {
  const location = useLocation()
  const [isParticipantPresent, setIsParticipantPresent] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Simulate loading
    setIsLoading(true)

    const checkParticipantStatus = () => {
      // Check if we're on demo route
      const isDemoRoute = location.pathname.includes('/demo')

      if (isDemoRoute) {
        // Mock: Random presence for demo route
        const randomPresence = Math.random() > 0.5
        setIsParticipantPresent(randomPresence)
      } else {
        setIsParticipantPresent(false)
      }

      setIsLoading(false)
    }

    // Simulate network delay
    const timeoutId = setTimeout(checkParticipantStatus, 500)

    return () => clearTimeout(timeoutId)
  }, [location.pathname])

  return {
    isParticipantPresent,
    isLoading
  }
}
